import type { Editor } from "@tiptap/react";
import type { FormulaRefPickerFragment } from "../client/generated";
import { useState } from "react";
import { Button } from "@acdc2/ui/components/button";
import { Command as CommandPrimitive } from "cmdk";
import { useTranslation } from "react-i18next";
import { getRegionName } from "../types/utils";
import { RegionThumbnail } from "../regions/RegionThumbnail";
import { Icon } from "@mdi/react";
import { mdiMathIntegral } from "@mdi/js";
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandInput,
} from "@acdc2/ui/components/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acdc2/ui/components/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@acdc2/ui/components/popover";

type Props = {
  fragment: FormulaRefPickerFragment;
  editor: Editor;
};

export function FormulaRefPicker({ editor, fragment }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleSelect = (regionId: string) => {
    editor.commands.setFormulaRef(regionId);
    setOpen(false);
  };

  const selectableRegions = fragment.regions.filter((region) =>
    region.attributes.some((attr) => attr.__typename === "ExpressionAttribute"),
  );

  const sortedSelectableRegions = selectableRegions.sort((a, b) => {
    const aIndex = a.readingOrderIndex ?? 0;
    const bIndex = b.readingOrderIndex ?? 0;
    return aIndex - bIndex;
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                className="size-8"
                variant="ghost"
                size="sm"
                role="combobox"
                aria-expanded={open}
              >
                <Icon path={mdiMathIntegral} size="1.5em" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t("FormulaRefPicker.tooltip")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="p-0 mx-2 w-auto min-w-72">
        <Command>
          <CommandInput placeholder={t("FormulaRefPicker.input")} />
          <CommandList>
            <CommandEmpty>{t("FormulaRefPicker.empty")}</CommandEmpty>
            {sortedSelectableRegions.map((region) => {
              const expressionAttr = region.attributes.find(
                (attr) => attr.__typename === "ExpressionAttribute",
              );

              const regionName = getRegionName(region);

              return (
                <CommandGroup key={region.id} heading={regionName}>
                  <CommandPrimitive.Item
                    value={expressionAttr?.expressionLatex}
                    keywords={[regionName]}
                    onSelect={() => handleSelect(region.id)}
                    className="px-2 py-1.5 cursor-pointer hover:bg-accent"
                  >
                    <RegionThumbnail fragment={region} />
                  </CommandPrimitive.Item>
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
